<template>
  <!-- 可拖动悬浮按钮 -->
  <transition name="fade">
    <div
      v-show="visible"
      ref="buttonRef"
      class="floating-ai-button"
      :class="{
        'floating-ai-button--mobile': isMobile,
        'floating-ai-button--hidden': !visible,
        'floating-ai-button--dragging': isDragging
      }"
      :style="buttonStyle"
      @click="handleClick"
      @mousedown="handleMouseDown"
      @touchstart="handleTouchStart"
    >
      <!-- AI助手图标 -->
      <div class="floating-ai-icon">
        <img :src="assistantGif" alt="开发者助手" />
      </div>

      <!-- 文字标签（可选） -->
      <transition name="slide">
        <span
          v-if="showLabel && !isDragging"
          class="floating-ai-label"
        >
          AI助手
        </span>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SimpleFloatingAIButton',

  props: {
    // 是否显示按钮
    visible: {
      type: Boolean,
      default: true
    },

    // 是否显示文字标签
    showLabel: {
      type: Boolean,
      default: false
    },

    // 按钮位置
    position: {
      type: Object,
      default: () => ({
        bottom: 144,
        right: 12
      })
    },

    // AI助手路由路径
    aiRoute: {
      type: [String, Object],
      default: '/assistant'
    },

    // 是否可拖动
    draggable: {
      type: Boolean,
      default: true
    },

    // 是否自动隐藏（滚动时）
    autoHide: {
      type: Boolean,
      default: false
    },

    // 是否启用边界吸附
    snapToEdge: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isMobile: false,
      isScrolling: false,
      isDragging: false,
      scrollTimeout: null,
      assistantGif: require("@/assets/AI/developer-assistant.gif"),
      // 当前位置
      currentX: 0,
      currentY: 0,
      // 拖动起始位置
      startX: 0,
      startY: 0,
      // 是否点击（区分拖动和点击）
      isClick: true,
      // 按钮尺寸
      buttonSize: 66
    }
  },

  computed: {
    buttonStyle() {
      return {
        bottom: `${this.currentY}px`,
        right: `${this.currentX}px`
      }
    }
  },

  watch: {
    visible(newVal) {
      if (newVal && this.autoHide) {
        this.setupScrollListener()
      }
    },

    position: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.isDragging) {
          this.currentX = newVal.right || 12;
          this.currentY = newVal.bottom || 144;
        }
      }
    }
  },

  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)

    // 初始化位置
    this.currentX = this.position.right ||12;
    this.currentY = this.position.bottom || 144;

    if (this.visible && this.autoHide) {
      this.setupScrollListener()
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
    window.removeEventListener('touchmove', this.handleTouchMove)
    window.removeEventListener('touchend', this.handleTouchEnd)
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }
  },

  methods: {
    // 检查是否为移动端
    checkMobile() {
      this.isMobile = window.innerWidth < 768
      this.buttonSize = this.isMobile ? 48 : 66
    },

    // 处理点击事件
    handleClick(e) {
      if (!this.isClick || !this.draggable) {
        return
      }

      this.goToAIAssistant()
    },

    // 跳转到AI助手页面
    goToAIAssistant() {
      const route = typeof this.aiRoute === 'string' ? this.aiRoute : '/assistant';

      // 添加 source=tips 参数标识为特殊拉起
      const targetUrl = `${route}?source=tips`;

      if (typeof this.aiRoute === 'string') {
        window.location.hash = '#' + targetUrl;
      } else {
        this.$router.push(targetUrl);
      }

      // 触发点击事件
      this.$emit('click')
    },

    // 鼠标按下事件
    handleMouseDown(e) {
      if (!this.draggable) return

      this.isDragging = true
      this.isClick = true
      this.startX = e.clientX
      this.startY = e.clientY

      window.addEventListener('mousemove', this.handleMouseMove)
      window.addEventListener('mouseup', this.handleMouseUp)

      e.preventDefault()
    },

    // 鼠标移动事件
    handleMouseMove(e) {
      if (!this.isDragging) return

      const deltaX = e.clientX - this.startX
      const deltaY = e.clientY - this.startY

      // 如果移动超过5px，则认为是拖动而不是点击
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.isClick = false
      }

      // 计算新位置
      let newX = this.currentX - deltaX
      let newY = this.currentY - deltaY

      // 限制边界
      newX = Math.max(0, Math.min(newX, window.innerWidth - this.buttonSize))
      newY = Math.max(0, Math.min(newY, window.innerHeight - this.buttonSize))

      this.currentX = newX
      this.currentY = newY
      this.startX = e.clientX
      this.startY = e.clientY
    },

    // 鼠标释放事件
    handleMouseUp(e) {
      if (!this.isDragging) return

      // 吸附到边缘
      if (this.snapToEdge) {
        this.snapToClosestEdge()
      }

      this.isDragging = false
      window.removeEventListener('mousemove', this.handleMouseMove)
      window.removeEventListener('mouseup', this.handleMouseUp)

      // 触发位置更新事件
      this.$emit('position-change', {
        right: this.currentX,
        bottom: this.currentY
      })
    },

    // 触摸开始事件
    handleTouchStart(e) {
      if (!this.draggable) return

      const touch = e.touches[0]
      this.isDragging = true
      this.isClick = true
      this.startX = touch.clientX
      this.startY = touch.clientY

      window.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      window.addEventListener('touchend', this.handleTouchEnd)
    },

    // 触摸移动事件
    handleTouchMove(e) {
      if (!this.isDragging) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - this.startX
      const deltaY = touch.clientY - this.startY

      // 如果移动超过5px，则认为是拖动而不是点击
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.isClick = false
      }

      // 计算新位置
      let newX = this.currentX - deltaX
      let newY = this.currentY - deltaY

      // 限制边界
      newX = Math.max(0, Math.min(newX, window.innerWidth - this.buttonSize))
      newY = Math.max(0, Math.min(newY, window.innerHeight - this.buttonSize))

      this.currentX = newX
      this.currentY = newY
      this.startX = touch.clientX
      this.startY = touch.clientY

      e.preventDefault()
    },

    // 触摸结束事件
    handleTouchEnd() {
      if (!this.isDragging) return

      // 吸附到边缘
      if (this.snapToEdge) {
        this.snapToClosestEdge()
      }

      this.isDragging = false
      window.removeEventListener('touchmove', this.handleTouchMove)
      window.removeEventListener('touchend', this.handleTouchEnd)

      // 触发位置更新事件
      this.$emit('position-change', {
        right: this.currentX,
        bottom: this.currentY
      })
    },

    // 吸附到最近的边缘
    snapToClosestEdge() {
      const windowWidth = window.innerWidth
      const buttonCenterX = this.currentX + this.buttonSize / 2

      // 判断应该吸附到左边还是右边
      if (buttonCenterX < windowWidth / 2) {
        // 吸附到左边
        this.currentX = 12
      } else {
        // 吸附到右边
        this.currentX = windowWidth - this.buttonSize
      }
    },

    // 设置滚动监听
    setupScrollListener() {
      window.addEventListener('scroll', this.handleScroll)
    },

    // 处理滚动事件
    handleScroll() {
      this.isScrolling = true

      // 滚动时隐藏标签
      if (this.showLabel) {
        this.$emit('update:showLabel', false)
      }

      // 清除之前的定时器
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }

      // 滚动停止后显示标签
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false
        if (this.showLabel) {
          this.$emit('update:showLabel', true)
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.floating-ai-button {
  position: fixed;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 90px;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.3s ease;
  user-select: none;
  touch-action: none;
}

.floating-ai-button:hover {
  transform: scale(1.1);
}

.floating-ai-button--dragging {
  cursor: grabbing;
  opacity: 0.8;
  transition: none;
}

.floating-ai-button--mobile {
  width: 48px;
  height: 48px;
}

.floating-ai-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.floating-ai-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.floating-ai-icon i {
  color: white;
}

.floating-ai-button--mobile .floating-ai-icon i {
  font-size: 20px;
}

/* 文字标签样式 */
.floating-ai-label {
  position: absolute;
  right: 70px;
  background: white;
  color: #333;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.floating-ai-button--mobile .floating-ai-label {
  right: 60px;
  font-size: 12px;
  padding: 4px 8px;
}

.floating-ai-label::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -4px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 5px solid white;
}

/* 动画效果 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter, .slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* 脉冲动画效果（可选） */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
}

.floating-ai-button--pulse {
  animation: pulse 2s infinite;
}
</style>